import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const FriendSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const searchRef = useRef(null);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Debounce search
    const delayDebounce = setTimeout(() => {
      if (searchTerm.trim() && isSearching) {
        searchUsers();
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm, isSearching]);

  const searchUsers = async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${process.env.REACT_APP_DATABASE_URL}/api/users/search?q=${encodeURIComponent(searchTerm)}`);
      setSearchResults(response.data);
      setIsOpen(true);
    } catch (err) {
      console.error('Error searching users:', err);
      setError('Failed to search for users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendFriendRequest = async (userId) => {
    try {
      await axios.post(`${process.env.REACT_APP_DATABASE_URL}/api/users/friends/request`, { userId });
      
      // Update the UI to show pending request
      setSearchResults(prev => 
        prev.map(user => 
          user.id === userId 
            ? { ...user, requestSent: true } 
            : user
        )
      );
    } catch (err) {
      console.error('Error sending friend request:', err);
      setError('Failed to send friend request');
    }
  };

  const handleFocus = () => {
    setIsSearching(true);
    if (searchTerm.trim()) {
      searchUsers();
    }
  };

  return (
    <div className="relative" ref={searchRef}>
      <div className="relative">
        <input
          type="text"
          placeholder="Search for users by name or @username"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={handleFocus}
          className="w-full md:w-64 px-4 py-2 pl-10 pr-8 rounded-full bg-gray-100 focus:bg-white border border-gray-300 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-300 text-sm"
        />
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        {searchTerm && (
          <button
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
            onClick={() => {
              setSearchTerm('');
              setSearchResults([]);
              setIsOpen(false);
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        {isLoading && (
          <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
            <svg className="animate-spin h-4 w-4 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (
        <div className="absolute mt-1 w-full bg-white rounded-lg shadow-lg max-h-96 overflow-auto z-50">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border-b border-gray-100">
              {error}
            </div>
          )}

          {searchResults.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {searchTerm.trim() ? (
                <>
                  <p className="mb-2">No users found with that name or username</p>
                  <button
                    className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium"
                    onClick={() => setIsOpen(false)}
                  >
                    Search for someone else
                  </button>
                </>
              ) : (
                'Type a name or username to search for friends'
              )}
            </div>
          ) : (
            <ul>
              {searchResults.map(user => (
                <li key={user.id} className="border-b last:border-0">
                  <div className="p-3 hover:bg-gray-50 flex items-center justify-between">
                    <Link
                      to={`/profile/${user.username}`}
                      className="flex items-center flex-grow"
                      onClick={() => setIsOpen(false)}
                    >
                      <div className="h-10 w-10 rounded-full bg-purple-200 flex items-center justify-center overflow-hidden mr-3">
                        {user.profilePhotoUrl ? (
                          <img src={user.profilePhotoUrl} alt={user.name} className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-sm font-medium text-purple-700">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">@{user.username}</p>
                      </div>
                    </Link>
                    <div>
                      {user.isFriend ? (
                        <span className="px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          Friends
                        </span>
                      ) : user.requestSent ? (
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          Request Sent
                        </span>
                      ) : (
                        <button
                          onClick={() => handleSendFriendRequest(user.id)}
                          className="px-3 py-1 bg-purple-600 text-white text-xs rounded-full hover:bg-purple-700"
                        >
                          Add Friend
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default FriendSearch;